import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import BookingCalendar from '@/components/feature/BookingCalendar';
import EmergencyBanner from '@/pages/home/components/EmergencyBanner';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz';

const RANDEVU_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Online Randevu', item: `${SITE_URL}/randevu` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Online Randevu | Van Polat Tesisat',
    description:
      'Van Polat Tesisat\'tan online randevu alın. Tarih ve saat seçin, tesisatçımız belirlenen vakitte kapınızda olsun.',
    url: `${SITE_URL}/randevu`,
    inLanguage: 'tr-TR',
    potentialAction: {
      '@type': 'ReserveAction',
      name: 'Randevu Al',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/randevu`,
      },
    },
  },
];

export default function RandevuPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <SeoHead
        title="Online Randevu | Van Polat Tesisat"
        description="Van Polat Tesisat'tan online randevu alın. Tarih ve saat seçin, tesisat ustamız belirlenen vakitte kapınızda. Ücretsiz keşif, garantili hizmet."
        keywords="van tesisat randevu, online randevu van tesisatçı, van tesisat saat al, van su kaçağı randevu"
        canonical="/randevu"
        schema={RANDEVU_SCHEMA}
      />
      <Navbar />
      <EmergencyBanner />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://public.readdy.ai/ai/img_res/3f62996834d5a5e719894c5932a2f537.jpg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-900/70 to-blue-800/90" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4 border border-white/20">
            <i className="ri-calendar-check-line"></i> Online Randevu
          </span>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Randevu Alın
          </h1>
          <p className="text-blue-200 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Müsait tarih ve saati seçin, kısa bilgilerinizi girin — ustamız belirlenen vakitte yanınızda olsun.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-1 bg-gray-50 pb-20 md:pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Booking Calendar */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-xl">
                    <i className="ri-calendar-2-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Tarih & Saat Seçin</h2>
                    <p className="text-xs text-gray-500">Müsait günler mavi renkte gösterilmektedir</p>
                  </div>
                </div>
                <BookingCalendar />
              </div>
            </div>

            {/* Sidebar info */}
            <div className="space-y-5">
              {/* Quick contact */}
              <div className="bg-white rounded-2xl p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Hızlı İletişim</p>
                <div className="space-y-3">
                  {[
                    { name: 'Engin POLAT', phone: '0507 607 4165', href: 'tel:+905076074165', wa: 'https://wa.me/905076074165?text=Merhaba%2C%20randevu%20almak%20istiyorum.' },
                    { name: 'Hamza POLAT', phone: '0543 820 7155', href: 'tel:+905438207155', wa: 'https://wa.me/905438207155?text=Merhaba%2C%20randevu%20almak%20istiyorum.' },
                    { name: 'Hayrullah POLAT', phone: '0543 641 2016', href: 'tel:+905436412016', wa: 'https://wa.me/905436412016?text=Merhaba%2C%20randevu%20almak%20istiyorum.' },
                  ].map((m) => (
                    <div key={m.name} className="flex items-center justify-between gap-2 border border-gray-100 rounded-xl px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-800 truncate">{m.name}</p>
                        <a href={m.href} className="text-xs text-blue-600 hover:text-blue-800 transition-colors">{m.phone}</a>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <a href={m.href} className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer">
                          <i className="ri-phone-fill text-xs"></i>
                        </a>
                        <a href={m.wa} target="_blank" rel="nofollow noreferrer" className="w-8 h-8 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors cursor-pointer">
                          <i className="ri-whatsapp-line text-xs"></i>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Working hours */}
              <div className="bg-white rounded-2xl p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Çalışma Saatleri</p>
                <ul className="space-y-2.5 text-sm">
                  {[
                    { day: 'Pazartesi – Cuma', time: '07:00 – 22:00' },
                    { day: 'Cumartesi', time: '08:00 – 20:00' },
                    { day: 'Pazar', time: '09:00 – 18:00' },
                    { day: 'Acil Durumlar', time: '7/24 WhatsApp' },
                  ].map((r) => (
                    <li key={r.day} className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 text-xs">{r.day}</span>
                      <span className="text-gray-900 font-semibold text-xs whitespace-nowrap">{r.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Info card */}
              <div className="bg-blue-600 rounded-2xl p-5 text-white">
                <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full mb-3">
                  <i className="ri-information-line text-white text-lg"></i>
                </div>
                <p className="text-sm font-bold mb-2">Randevu Hakkında</p>
                <ul className="space-y-1.5 text-xs text-blue-100">
                  <li className="flex items-start gap-1.5"><i className="ri-check-line shrink-0 mt-0.5"></i> Online randevu ücretsizdir</li>
                  <li className="flex items-start gap-1.5"><i className="ri-check-line shrink-0 mt-0.5"></i> Onay SMS ile gönderilir</li>
                  <li className="flex items-start gap-1.5"><i className="ri-check-line shrink-0 mt-0.5"></i> 24 saat önceden iptal edebilirsiniz</li>
                  <li className="flex items-start gap-1.5"><i className="ri-check-line shrink-0 mt-0.5"></i> Yerinde ücretsiz keşif yapılır</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
